import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { submitDailyPhotoContestGuess } from '../../store/thunks/contestThunks';

const GuessThePhoto = () => {
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [guessStatus, setGuessStatus] = useState(null); // 'submitting', 'success', 'error'
    const dispatch = useDispatch();

    const { entries } = useSelector(state => state.entries);
    const { dailyContent, isLoading, error } = useSelector(state => state.contest);

    // Check if user has already guessed today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const hasGuessedForToday = entries?.some(entry => {
        const entryDate = new Date(entry.timestamp);
        entryDate.setHours(0, 0, 0, 0);
        return entry.entryType === 'daily_photo' && entryDate.getTime() === today.getTime();
    });

    const handleAnswerSelect = async (answer) => {
        setSelectedAnswer(answer);
        setGuessStatus('submitting');
        try {
            const result = await dispatch(submitDailyPhotoContestGuess({
                answer
            })).unwrap();
            setGuessStatus('success');
        } catch (err) {
            setGuessStatus('error');
            // Reset selected answer if submission fails so user can try again
            setSelectedAnswer(null);
        }
    };

    const calculateDailyTitle = () => {
        const dayOfWeek = new Date().getDay();
        // Convert Sunday (0) to 7, keep other days as 1-6
        const dayNumber = dayOfWeek === 0 ? 7 : dayOfWeek;
        return `Week ${dailyContent?.week} - Day ${dayNumber}`;
    }

    const calculateDailyEntries = () => {
        const dayOfWeek = new Date().getDay();
        const entriesByDay = {
            1: 8, // Monday
            2: 7, // Tuesday 
            3: 6, // Wednesday
            4: 5, // Thursday
            5: 4, // Friday
            6: 3, // Saturday
            0: 2  // Sunday
        };
        return entriesByDay[dayOfWeek] || 0;
    }

    if (isLoading) {
        return <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>;
    }

    return (
        <div className="max-w-2xl mx-auto p-6 bg-white rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold text-center mb-6">{calculateDailyTitle()}</h2>
            <h2 className="text-xl font-bold text-center mb-6">Earn {calculateDailyEntries()} entries for a correct guess today!</h2>
            <div className="mb-6">
                <div className="relative aspect-video rounded-lg overflow-hidden shadow-lg">
                    <img
                        src="/daily-photo.jpg"
                        alt="Guess the location"
                        className="w-full h-full object-cover"
                    />
                </div>
            </div>

            <div className="flex flex-col space-y-4">
                {hasGuessedForToday ? (
                    <div className="text-center p-4">
                        You've already submitted your guess for today. Come back tomorrow!
                    </div>
                ) : (
                    dailyContent?.options?.map((option, index) => (
                        <button
                            key={index}
                            onClick={() => handleAnswerSelect(option)}
                            className={`w-full p-4 text-left rounded-lg transition-colors duration-200 
                                ${selectedAnswer === option 
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 hover:bg-gray-200'
                                }
                                ${(selectedAnswer !== null || guessStatus === 'success') ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={selectedAnswer !== null || guessStatus === 'success'}
                        >
                            {option}
                        </button>
                    ))
                )}
            </div>

            {guessStatus === 'submitting' && (
                <div className="mt-6 p-4 rounded-lg text-center text-white bg-blue-500">
                    Submitting your guess...
                </div>
            )}

            {guessStatus === 'success' && (
                <div className="mt-6 p-4 rounded-lg text-center text-white bg-green-500">
                    Your guess has been accepted! To keep the contest fair, we'll reveal the correct answer next week.
                    Come back tomorrow and guess again to earn more entries into the contest! You may want to change your
                    guess once you see more of the image.
                </div>
            )}

            {guessStatus === 'error' && (
                <div className="mt-6 p-4 rounded-lg text-center text-white bg-red-500">
                    There was an error submitting your guess. Please try again.
                </div>
            )}
        </div>
    );
};

export default GuessThePhoto;
